/**
 * Minimal markdown-lite renderer used by the policy pages.
 * Supports just what the admin needs to write comfortably:
 *
 *   ## Heading        → <h2>
 *   - list item       → bullet
 *   **bold text**     → <strong>
 *   blank line        → paragraph break
 *
 * No external dependency, no HTML injection (text only inside elements).
 */
import React from "react";

function renderInline(text) {
  // Split on **bold** while preserving the segments.
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((seg, i) => {
    if (/^\*\*[^*]+\*\*$/.test(seg)) {
      return <strong key={i}>{seg.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={i}>{seg}</React.Fragment>;
  });
}

export default function MarkdownLite({ source = "" }) {
  const lines = source.replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.trim() === "") {
      i += 1;
      continue;
    }

    if (line.startsWith("## ")) {
      blocks.push({ type: "h2", text: line.slice(3).trim() });
      i += 1;
      continue;
    }

    if (line.startsWith("- ")) {
      const items = [];
      while (i < lines.length && lines[i].startsWith("- ")) {
        items.push(lines[i].slice(2).trim());
        i += 1;
      }
      blocks.push({ type: "ul", items });
      continue;
    }

    // Otherwise it's a paragraph — collect until blank line or block boundary.
    const paragraphLines = [];
    while (
      i < lines.length &&
      lines[i].trim() !== "" &&
      !lines[i].startsWith("## ") &&
      !lines[i].startsWith("- ")
    ) {
      paragraphLines.push(lines[i]);
      i += 1;
    }
    blocks.push({ type: "p", text: paragraphLines.join(" ").trim() });
  }

  return (
    <>
      {blocks.map((b, idx) => {
        if (b.type === "h2") return <h2 key={idx}>{renderInline(b.text)}</h2>;
        if (b.type === "ul") {
          return (
            <ul key={idx}>
              {b.items.map((it, j) => (
                <li key={j}>{renderInline(it)}</li>
              ))}
            </ul>
          );
        }
        return <p key={idx}>{renderInline(b.text)}</p>;
      })}
    </>
  );
}
